import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import axios from "axios";

import {
  MediaLibraryResourceType,
  type MediaLibraryItem,
  type MediaLibraryVideoData,
} from "@fitness-app/data-models/entities/MediaLibrary";
import {
  STUDIO_CLIPS_AGENT_TYPE,
  type StudioClip,
  type StudioClipStatus,
} from "@fitness-app/data-models/entities/StudioClip";
import { getErrorMessage } from "@fitness-app/utils";

import { useAuth } from "~/modules/Auth/AuthProvider";
import { backgroundTasksApi, supabase } from "~/store/initializeStore";
import { acknowledgeStudioProduction, getStudioProductionRequestId } from "./studio-production-intent";
import { type StudioRequest } from "./studio-requests.logic";

export const STUDIO_SOURCES_QUERY_KEY = ["studio", "sources"];
export const STUDIO_CLIPS_QUERY_KEY = ["studio", "clips"];
export const STUDIO_REQUESTS_QUERY_KEY = ["studio", "requests"];

export interface StudioClipRow extends StudioClip {
  clipMedia: { id: string; title: string; data: MediaLibraryVideoData } | null;
  source: { id: string; title: string } | null;
}

export interface ProduceClipsResponse {
  run: { id: string } | null;
  accessToken: string | null;
  status: string;
  requestId: string;
  tags: string[];
}

/** Długie nagrania wrzucone jako surowiec Klipowni. */
export const useStudioSources = () =>
  useQuery(STUDIO_SOURCES_QUERY_KEY, {
    queryFn: async () => {
      const { data, error } = await supabase
        .from("media_library")
        .select("*")
        .eq("resourceType", MediaLibraryResourceType.StudioSource)
        .order("createdAt", { ascending: false });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as MediaLibraryItem[];
    },
  });

export const useStudioClips = () =>
  useQuery(STUDIO_CLIPS_QUERY_KEY, {
    queryFn: async () => {
      const { data, error } = await supabase
        .from("studio_clip")
        .select(
          "*, clipMedia:media_library!studio_clip_clipMediaId_fkey(id,title,data), source:media_library!studio_clip_sourceMediaId_fkey(id,title)",
        )
        .order("createdAt", { ascending: false })
        .order("score", { ascending: false });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as StudioClipRow[];
    },
  });

/** Przebiegi Klipowni — do pokazania postępu przy nagraniu i blokady podwójnego startu. */
export const useStudioRequests = () =>
  useQuery(STUDIO_REQUESTS_QUERY_KEY, {
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_agent_request")
        .select("id, taskId, status, taskCount, data, createdAt")
        .eq("agentType", STUDIO_CLIPS_AGENT_TYPE)
        .order("createdAt", { ascending: false })
        .limit(30);

      if (error) throw new Error(error.message);
      return (data ?? []) as StudioRequest[];
    },
  });

/**
 * Jedna subskrypcja realtime dla całego modułu: każda zmiana w klipach albo bibliotece
 * mediów (webhook Bunny podmienia statusy wideo) tylko unieważnia zapytania react-query.
 */
export const useStudioRealtime = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("studio-panel")
      .on("postgres_changes", { event: "*", schema: "public", table: "studio_clip" }, () => {
        void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "studio_segment" }, () => {
        void queryClient.invalidateQueries(["studio", "segments"]);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "media_library" }, () => {
        void queryClient.invalidateQueries(STUDIO_SOURCES_QUERY_KEY);
        void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);
};

/**
 * Removes a recording whose upload never went through (TRE-2108). The row is fenced off from
 * the browser by RLS, so the server route does it after the usual Studio authorization.
 */
export const useDeleteStudioSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const response = await backgroundTasksApi.post<{ mediaId: string }>("/api/trainers/ai/delete-studio-source", {
        mediaId,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(STUDIO_SOURCES_QUERY_KEY);
    },
    onError: (error) => {
      void message.error(`Nie udało się usunąć nagrania: ${getErrorMessage(error)}`);
    },
  });
};

export const useProduceClips = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      sourceMediaId: string;
      maxClips?: number;
      /** segments (etap G): mine hook / value / cta blocks for ads instead of standalone clips. */
      mode?: "clips" | "segments";
      perRole?: number;
    }): Promise<ProduceClipsResponse> => {
      if (!user) throw new Error("Zaloguj się ponownie.");
      const requestId = getStudioProductionRequestId(
        window.localStorage,
        user.id,
        payload.sourceMediaId,
        payload.mode ?? "clips",
      );
      try {
        const response = await backgroundTasksApi.post<ProduceClipsResponse>("/api/trainers/ai/produce-video-clips", {
          ...payload,
          requestId,
        });
        acknowledgeStudioProduction(
          window.localStorage,
          user.id,
          payload.sourceMediaId,
          requestId,
          payload.mode ?? "clips",
        );
        return response.data;
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status && status >= 400 && status < 500) {
          acknowledgeStudioProduction(
            window.localStorage,
            user.id,
            payload.sourceMediaId,
            requestId,
            payload.mode ?? "clips",
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.refetchQueries({ queryKey: ["ai_agent_request"] });
      void queryClient.refetchQueries({ queryKey: STUDIO_REQUESTS_QUERY_KEY });
      void message.success("Produkcja ruszyła w tle — status sprawdzisz przy nagraniu.");
    },
    onError: (error) => {
      void message.error(`Nie udało się rozpocząć produkcji klipów: ${getErrorMessage(error)}`);
    },
  });
};

/** Decyzja człowieka: RLS pozwala z panelu zmienić wyłącznie status (i updatedAt). */
export const useUpdateClipStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clipId, status }: { clipId: string; status: StudioClipStatus }) => {
      const { error } = await supabase
        .from("studio_clip")
        .update({ status, updatedAt: new Date().toISOString() })
        .eq("id", clipId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
    },
    onError: (error) => {
      void message.error(`Nie udało się zapisać decyzji: ${getErrorMessage(error)}`);
    },
  });
};
